import {
  Injectable,
  NgZone,
  PLATFORM_ID,
  Inject,
  Renderer2,
  RendererFactory2,
  DOCUMENT,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, firstValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ToastService } from '../toast/toast.service';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user-profile/user-profile.service';
import { NotificationService } from '../../../core/firebase/notification.service';
import { AuthService } from '../auth/auth.service';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: any) => any;
        };
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: (callback?: () => void) => void;
        };
      };
    };
  }
}

export interface GoogleOAuthResponse {
  success: boolean;
  token?: string;
  error?: string;
  user?: {
    email: string;
    name: string;
    picture?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class GoogleOAuthService {
  private static readonly OAUTH_PROCESSING_KEY = 'google_oauth_processing';

  private googleClientId: string = '';
  private redirectUri: string = '';
  private codeClient: any = null;
  private scriptLoaded = new BehaviorSubject<boolean>(false);
  private renderer: Renderer2;

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
    private toastService: ToastService,
    private apiService: ApiService,
    private userProfileService: UserProfileService,
    private notificationService: NotificationService,
    private authService: AuthService,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Renderer2 isn't injectable in services; create it from RendererFactory2
    this.renderer = rendererFactory.createRenderer(null, null);

    // Only load Google script in browser, not during SSR
    if (isPlatformBrowser(this.platformId)) {
      this.loadGoogleScript();
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** Uses current site origin so one build works on localhost, dev, test, and prod. */
  private resolveRedirectUri(): string {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/auth/callback`;
    }
    return '';
  }

  private stripOAuthQueryFromUrl(): void {
    if (!this.isBrowser() || typeof window === 'undefined') {
      return;
    }
    const path = window.location.pathname || '/auth/callback';
    window.history.replaceState({}, this.document.title, path);
  }

  /**
   * Load Google Identity Services script dynamically
   */
  private loadGoogleScript(): void {
    // Check if we're in browser
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    if (window.google) { 
      this.scriptLoaded.next(true);
      this.initializeOAuth();
      return;
    }

    // Use Angular's Renderer2 to create and append script element
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'src', 'https://accounts.google.com/gsi/client');
    this.renderer.setAttribute(script, 'async', 'true');
    this.renderer.setAttribute(script, 'defer', 'true');
    
    this.renderer.listen(script, 'load', () => {
      this.ngZone.run(() => {
        this.scriptLoaded.next(true);
        this.initializeOAuth();
      });
    });
    
    this.renderer.listen(script, 'error', () => {
      console.error('Failed to load Google Identity Services script');
      this.scriptLoaded.next(false);
    });
    
    this.renderer.appendChild(this.document.head, script);
  }

  /**
   * Initialize Google OAuth with PKCE flow
   */
  private initializeOAuth(): void {
    // Check if we're in browser
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    if (!window.google) {
      console.error('Google Identity Services not loaded');
      return;
    }

    this.googleClientId = environment.googleClientId || '';
    this.redirectUri = this.resolveRedirectUri();

    if (!this.googleClientId) {
      console.warn('Google Client ID not configured. Please add googleClientId to environment.');
      return;
    }

    try {
      // Initialize code client for PKCE flow
      this.codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: this.googleClientId,
        scope: 'email profile openid',
        ux_mode: 'redirect',
        redirect_uri: this.redirectUri,
        // PKCE is automatically handled by Google Identity Services
      });
    } catch (error) {
      console.error('Failed to initialize Google OAuth client:', error);
    }
  }

  /**
   * Check if Google OAuth is ready
   */
  isReady(): Observable<boolean> {
    return this.scriptLoaded.asObservable();
  }

  /**
   * Initiate Google OAuth login flow (redirects to Google)
   */
  loginWithGoogle(): void {
    // Only work in browser
    if (!isPlatformBrowser(this.platformId)) {
      console.error('Google OAuth is only available in browser');
      return;
    }

    if (!this.codeClient) {
      this.isReady().subscribe((ready) => {
        if (ready && this.codeClient) {
          this.ngZone.run(() => {
            this.codeClient.requestCode();
          });
        } else {
          console.error('Google OAuth not initialized. Please check your configuration.');
        }
      });
      return;
    }

    this.ngZone.run(() => {
      this.codeClient.requestCode();
    });
  }

  /**
   * Handle OAuth callback - exchange authorization code with backend
   * This is called after Google redirects back to your app
   */
  handleCallback(code: string): Observable<GoogleOAuthResponse> {
    // Only work in browser
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('OAuth callback only works in browser'));
    }

    const redirectUri = this.redirectUri || this.resolveRedirectUri();

    const requestBody = {
      code: code,
      redirectUri,
    };

    // Use centralized API service but disable auto-toast (we'll handle it manually)
    // Pass only the endpoint path, not the full URL (ApiService will add baseUrl)
    return this.apiService.post<GoogleOAuthResponse>('/auth/google', requestBody, false).pipe(
      catchError((error: any) => {
        let errorMessage = 'Something went wrong. Please try again.';

        // Handle error from centralized API service (wrapped error)
        const httpError = error?.error || error;
        const status = error?.status || httpError?.status || 0;

        if (status === 0) {
          // Backend is down or network error
          errorMessage = 'Something went wrong. Please try again.';
          if (this.isBrowser()) {
            this.toastService.error(errorMessage, 'Connection Error');
          }
        } else if (status === 404) {
          errorMessage = 'Backend endpoint not found. Please implement /auth/google endpoint.';
          if (this.isBrowser()) {
            this.toastService.error(errorMessage, 'Error');
          }
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
          if (this.isBrowser()) {
            this.toastService.error(errorMessage, 'Server Error');
          }
        } else {
          // Extract error message from backend
          errorMessage =
            httpError?.detail ||
            httpError?.error ||
            httpError?.message ||
            'Authentication failed. Please try again.';
          if (this.isBrowser()) {
            this.toastService.error(errorMessage, 'Authentication Failed');
          }
        }

        console.error('OAuth callback HTTP error:', {
          status,
          message: errorMessage,
          error: httpError,
        });

        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  /**
   * Process OAuth callback and handle response
   */
  async processCallback(code: string): Promise<void> {
    // Only work in browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (sessionStorage.getItem(GoogleOAuthService.OAUTH_PROCESSING_KEY) === code) {
      return;
    }
    sessionStorage.setItem(GoogleOAuthService.OAUTH_PROCESSING_KEY, code);
    this.stripOAuthQueryFromUrl();

    try {
      const response = await firstValueFrom(this.handleCallback(code));

      if (response && response.success && response.token) {
        await this.authService.persistAccessToken(response.token);

        // Store user info if provided
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }

        sessionStorage.removeItem(GoogleOAuthService.OAUTH_PROCESSING_KEY);

        // Show success toast
        if (this.isBrowser()) {
          this.toastService.success('Login successful', 'Success');
        }

        // Refresh user profile, then register FCM token for push notifications
        this.userProfileService.fetchUserProfile(true).subscribe({
          next: (profile) => {
            if (profile?.id) {
              void this.notificationService.registerTokenAfterLogin(profile.id);
            }
          },
        });

        // Navigate to dashboard
        this.ngZone.run(() => {
          this.router.navigate(['/dashboard'], { replaceUrl: true });
        });
      } else {
        const errorMsg = response?.error || 'Google OAuth failed - invalid response';
        if (this.isBrowser()) {
          this.toastService.error(errorMsg, 'Authentication Failed');
        }
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('OAuth callback error:', error);

      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.removeItem(GoogleOAuthService.OAUTH_PROCESSING_KEY);
      }

      // Error toast is already shown in handleCallback catchError
      // Just navigate to login
      const errorMessage = error?.message || error?.toString() || 'Authentication failed';

      this.ngZone.run(() => {
        this.router.navigate(['/login'], {
          queryParams: {
            error: 'oauth_failed',
            message: errorMessage,
          },
          replaceUrl: true,
        });
      });
    }
  }

  /**
   * Check if we have a pending OAuth callback
   */
  checkPendingCallback(): void {
    // Only work in browser
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      this.ngZone.run(() => {
        this.router.navigate(['/login'], {
          queryParams: { error: 'google_oauth_error', message: error },
        });
      });
      return;
    }

    if (code) {
      if (sessionStorage.getItem(GoogleOAuthService.OAUTH_PROCESSING_KEY) === code) {
        return;
      }
      void this.processCallback(code);
    }
  }

  /**
   * Logout - clear Google OAuth and app tokens
   */
  logout(): void {
    // Only work in browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.authService.logout();

    this.notificationService.clearRegistrationState();
    sessionStorage.removeItem(GoogleOAuthService.OAUTH_PROCESSING_KEY);

    // Optionally revoke Google token (if stored)
    if (typeof window !== 'undefined' && window.google?.accounts?.oauth2) {
      // Note: We don't store Google tokens, so nothing to revoke
      // If you need to revoke, you'd need to store the access_token
    }
  }
}
